import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import * as checkoutStore from '@cartwhl/common/stores/checkoutStore';
import * as productStore from '@cartwhl/common/stores/productStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { getHomepageUrl } from '@cartwhl/common/utils';
import { getStripePortalSession } from '@cartwhl/common/utils/getStripePortalSession';
import { getStripeProductID } from '@cartwhl/common/utils/getStripeProductID';
import Switch from '@cartwhl/ui/atoms/Switch';
import Text from '@cartwhl/ui/base/Text';
import ArrowUpRightIcon from '@cartwhl/ui/icons/ArrowUpRightIcon';
import MinusIcon from '@cartwhl/ui/icons/MinusIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import PlanFeatures from './PlanFeatures';

export default function AddSeatModal({ close }: { close: () => void }) {
  const { products } = useSnapshot(productStore.state);
  const { organizationInfo, userInfo } = useSnapshot(userStore.state);
  const [quantity, setQuantity] = useState(1);
  const [isMonthly, setIsMonthly] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const savings = productStore.getSavings('pro');

  useEffect(() => {
    productStore.fetchProducts();
  }, []);

  function handleOpenChange(open: boolean) {
    if (!open) {
      close();
    }
  }

  const unlimitedProductID = useMemo(() => getStripeProductID('unlimited'), []);
  const unlimitedPrice = useMemo(
    () =>
      productStore.getPriceByProductAndMode(
        unlimitedProductID,
        isMonthly ? 'month' : 'year',
      ),
    [unlimitedProductID, isMonthly, productStore.state.prices.length],
  );

  const pricePerUnit = unlimitedPrice?.unit_amount ?? 0;
  const priceLabel = `$${(pricePerUnit / 100).toFixed(0)} / ${
    isMonthly ? 'mo' : 'yr'
  }`;

  const canCheckout = !!unlimitedPrice && quantity > 0;

  async function requestPortalSession() {
    setRequesting(true);
    const newTotalQuantity =
      (organizationInfo?.totalUnlimitedSeats ?? 0) + quantity;
    getStripePortalSession(
      userInfo.stripeCustomerID,
      unlimitedPrice?.id,
      newTotalQuantity,
    )
      .then(({ data }) => {
        if (typeof data !== 'undefined') {
          window.location.href = data.url;
        }
        setRequesting(false);
      })
      .catch(() => setRequesting(false));
  }

  const handleCheckout = async () => {
    if (!unlimitedPrice) return;
    if (
      organizationInfo?.totalProfessionalSeats === 0 &&
      organizationInfo?.totalUnlimitedSeats === 0
    ) {
      setRequesting(true);
      checkoutStore.select(unlimitedPrice, quantity);
      await checkoutStore.startCheckout();
      close();
    } else {
      requestPortalSession();
    }
  };

  return (
    <Modal
      title="Add a new seat"
      maxWidth={684}
      open={true}
      onOpenChange={handleOpenChange}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          {/* <Switch
            className="max-h-[40px] w-[320px] py-2 font-medium lg:flex"
            left={savings ? `Pay yearly — ${savings}% off` : 'Pay yearly'}
            right="Pay monthly"
            superscript={null}
            defaultValue={isMonthly}
            onChange={(setIsMonthly)}
            variant="web/body/md"
          /> */}

          <a
            className="ml-auto text-sm"
            href={getHomepageUrl('/pricing')}
            target="_blank"
            rel="noreferrer"
          >
            <div className="flex items-center gap-2">
              <Text as="p" variant="studio/body/md">
                View all full plan details
              </Text>
              <ArrowUpRightIcon />
            </div>
          </a>
        </div>

        {/* Unlimited Plan Row */}
        <div className="border-gray-30 dark:border-gray-60 flex items-center justify-between rounded-lg border p-4">
          <PlanFeatures plan="Unlimited" />

          <div className="flex items-center gap-4">
            <Text as="span" variant="studio/body/bold/md">
              {priceLabel}
            </Text>
            <div className="flex items-center gap-2 rounded-lg p-2">
              <Button
                className="dark:bg-gray-90 h-8 w-8 bg-white"
                icon={<MinusIcon />}
                variant="secondary"
                size="sm"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                aria-label="Decrease quantity"
              />
              <Text as="span" variant="studio/body/bold/md">
                {quantity}
              </Text>
              <Button
                className="dark:bg-gray-90 bg-white"
                icon={<PlusIcon />}
                variant="secondary"
                size="sm"
                onClick={() => setQuantity(quantity + 1)}
                aria-label="Increase quantity"
              />
            </div>
          </div>
        </div>

        <div className="mt-2 flex flex-row flex-nowrap">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            className="ml-auto"
            variant="primary"
            loading={requesting}
            onClick={handleCheckout}
            disabled={!canCheckout}
          >
            Add seats
          </Button>
        </div>
      </div>
    </Modal>
  );
}
